import type { LucideIcon } from "lucide-react";
import type { IconTileTone } from "@/components/hoursign/IconTile";
import { BadgeCheck, CalendarCheck, Download, House, Inbox, ListChecks, Users } from "lucide-react";

export type Mode = "practitioner" | "supervisor";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** The tile colour. Adjacent destinations never share one. */
  tone: IconTileTone;
  /** Only this exact path highlights the item. */
  end?: boolean;
  /** Shown in the bottom tab bar on mobile (max 4, plus "More"). */
  onMobile?: boolean;
  /** How many things here need you. Any number > 0 shows the attention dot. */
  badge?: number;
}

/** Practitioner navigation. Labels and icons: design system section 06. */
export function practitionerNav(trackId: string): NavItem[] {
  return [
    { to: `/t/${trackId}`, label: "Home", icon: House, tone: "primary", end: true, onMobile: true },
    { to: `/t/${trackId}/log`, label: "Log", icon: ListChecks, tone: "direct", onMobile: true },
    {
      to: `/t/${trackId}/periods`,
      label: "Periods",
      icon: CalendarCheck,
      tone: "individual",
      onMobile: true,
    },
    {
      to: `/t/${trackId}/exports`,
      label: "Exports",
      icon: Download,
      tone: "indirect",
      onMobile: true,
    },
    {
      to: `/t/${trackId}/people`,
      label: "Supervisors & sites",
      icon: Users,
      tone: "couples",
      onMobile: true,
    },
  ];
}

/** Supervisor navigation. Design system section 06. */
export function supervisorNav(): NavItem[] {
  return [
    { to: "/supervise", label: "To review", icon: Inbox, tone: "group", end: true, onMobile: true },
    {
      to: "/supervise/signed",
      label: "Signed records",
      icon: BadgeCheck,
      tone: "success",
      onMobile: true,
    },
  ];
}

export function navFor(mode: Mode, trackId: string | undefined): NavItem[] {
  if (mode === "supervisor") return supervisorNav();
  return trackId ? practitionerNav(trackId) : [];
}

/** The URL decides the mode, so a shared link always opens in the right one. */
export function modeFromPath(pathname: string): Mode {
  return pathname.startsWith("/supervise") ? "supervisor" : "practitioner";
}
